import { Table, Tag, Button, Space } from "antd";
import { useState } from "react";
import styles from "./index.module.scss";
import "./index.scss";
import { ShipmentTableProps } from "./types";
import moment from "moment";
import { SALES_ORDER_STATUS } from "utils/shipment";
import PackagingListModal from "../PackagingListModel";
import { DownloadOutlined, FileOutlined } from "@ant-design/icons";
import { getApi, getApiExcelforPdf, putApi } from "redux/apis";
import { toastText } from "utils/utils";
import { invalidText } from "utils/utils";
import SearchAndFilter from "./SearchAndFilter";
import { DeleteActionSvg, SortSvgBottom, SortSvgTop } from "utils/svgs";
import { generatePackingListHTML } from "utils/packingListReport";
import { generateDeliveryOrderHTML } from "utils/deliveryOrderReport";
import PrintDeliveryOrderModal from "../PrintDeliveryOrderModal";
import pdfMake from "pdfmake/build/pdfmake";
import htmlToPdfmake from "html-to-pdfmake";

// Initialize pdfmake fonts - required for PDF generation
// For pdfmake 0.2.x, fonts need to be set up before use
let fontsInitialized = false;

const initializePdfMakeFonts = async () => {
  if (fontsInitialized) return;
  
  try {
    // Try different import paths for pdfmake fonts
    // For pdfmake 0.2.x, fonts might be in different locations
    let pdfFonts: any = null;
    
    try {
      pdfFonts = await import("pdfmake/build/vfs_fonts");
    } catch (e1) {
      try {
        pdfFonts = await import("pdfmake/build/vfs_fonts.js");
      } catch (e2) {
        try {
          pdfFonts = require("pdfmake/build/vfs_fonts");
        } catch (e3) {
          // If all imports fail, we'll set up fonts manually or use defaults
          console.warn("Could not import pdfmake fonts, will try to proceed without custom fonts");
        }
      }
    }
    if (pdfFonts) {
      if (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
        fontsInitialized = true;
      } else if (pdfFonts.default && pdfFonts.default.pdfMake && pdfFonts.default.pdfMake.vfs) {
        pdfMake.vfs = pdfFonts.default.pdfMake.vfs;
        fontsInitialized = true;
      } else if (typeof pdfFonts === 'object' && 'Roboto-Regular.ttf' in pdfFonts) {
        // Direct vfs object
        pdfMake.vfs = pdfFonts;
        fontsInitialized = true;
      } else if (typeof pdfFonts === 'object' && 'Lexend-Regular.ttf' in pdfFonts) {
        pdfMake.vfs = pdfFonts;
        fontsInitialized = true;
      }
    }
    
    // If fonts still not loaded, set empty vfs (pdfmake will use built-in fonts if available)
    if (!fontsInitialized) {
      pdfMake.vfs = pdfMake.vfs || {};
      console.warn("Using pdfmake without custom fonts - PDF may have limited font support");
    }
  } catch (e) {
    console.error("Error initializing pdfmake fonts:", e);
    // Set empty vfs to prevent errors
    pdfMake.vfs = pdfMake.vfs || {};
  }
};

// Initialize fonts immediately
initializePdfMakeFonts();

const { Column } = Table;

const SortingIcon = (data: any) => {
  return data.data.sortOrder === "ascend" ? <SortSvgTop /> : <SortSvgBottom />;
};

const CompletedSalesOrderTable = (props: ShipmentTableProps) => {
  const {
    salesOrderData,
    isLoading,
    searchValue,
    performSearchHandler,
    performSortHandler,
    showModal,
    setSelectedPacking,
  } = props;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>();
  const [packagingData, setPackagingData] = useState<any[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<any>();
  const [loadingPaySlip, setLoadingPaySlip] = useState<Record<string, boolean>>(
    {}
  );
  const [isDeliveryOrderModalVisible, setIsDeliveryOrderModalVisible] = useState(false);
  const [deliveryOrderData, setDeliveryOrderData] = useState<any>(null);
  const [loadingDeliveryOrder, setLoadingDeliveryOrder] = useState<Record<string, boolean>>(
    {}
  );
  const [loadingPrint, setLoadingPrint] = useState(false);

  const tableChangeHandler = (pageInfo: any, d: any, columnInfo: any) => {
    performSortHandler &&
      performSortHandler(columnInfo.order, pageInfo.current);
  };

  const handleSalesOrderClick = async (
    salesOrder: string,
    packageId: string
  ) => {
    try {
      const response: any = await getApi(
        `/shipping/packaginglist?packageId=${packageId}`
      );
      const data = response.data.data;
      setPackagingData(Array.isArray(data.pallets) ? data.pallets : []);
      setSelectedOrder(salesOrder);
      setSelectedPackageId(packageId);
      setIsModalVisible(true);
    } catch (error) {
      console.error("Error fetching packaging data:", error);
    }
  };

  const exportPdf = async (packageId: string) => {
    setLoadingPaySlip((prev) => ({ ...prev, [packageId]: true }));

    try {
      // Fetch data from backend
      const response: any = await getApi(
        `/packing-list/pdf?packageId=${packageId}`
      );

      if (!response || response.status !== 200 || !response.data?.data) {
        throw new Error("Failed to fetch the packaging data.");
      }

      const data = response.data.data;
      const html = generatePackingListHTML(data.data, "");

      // Parse HTML to extract content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Helper function to convert images to base64
      const convertImageUrlToBase64 = async (url: string): Promise<string> => {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.warn("Failed to convert image to base64:", error);
          return url;
        }
      };

      // Process images - convert to base64
      const images = Array.from(doc.querySelectorAll("img"));
      for (const img of images) {
        const src = img.getAttribute("src");
        if (src && !src.startsWith("data:")) {
          try {
            const base64 = await convertImageUrlToBase64(src);
            img.setAttribute("src", base64);
          } catch (error) {
            console.warn("Failed to convert image:", src, error);
          }
        }
      }

      // Extract header data
      const logoImg = doc.querySelector(".header img");
      const logoSrc = logoImg?.getAttribute("src") || "";

      // Extract details section
      const section1Elements = doc.querySelectorAll(".details .section1");
      const firstSection = section1Elements[0];
      const secondSection = section1Elements[1];
      
      const currentDate = firstSection?.querySelector("p:first-child strong")?.nextSibling?.textContent?.trim() || "-";
      const salesOrderNo = firstSection?.querySelector("p:nth-child(2) strong")?.nextSibling?.textContent?.trim() || "-";
      const customerName = firstSection?.querySelector("p:nth-child(3) strong")?.nextSibling?.textContent?.trim() || "-";

      // Extract Ship To address from second section
      const shipToElement = secondSection?.querySelector("p:first-child");
      let shipToLines: string[] = [];
      if (shipToElement) {
        const shipToHtml = shipToElement.innerHTML;
        const parts = shipToHtml.split('<br>');
        shipToLines = parts
          .map((part, index) => {
            const cleaned = part.replace(/<[^>]*>/g, '').trim();
            // For the first part, extract text after "Ship To:"
            if (index === 0 && cleaned.includes('Ship To:')) {
              const afterShipTo = cleaned.split('Ship To:')[1]?.trim();
              return afterShipTo || null;
            }
            return cleaned;
          })
          .filter(part => part && part.length > 0);
      }

      // Extract Ship Via from second section
      const shipVia = secondSection?.querySelector("p:nth-child(2) strong")?.nextSibling?.textContent?.trim() || "N/A";

      // Extract pallet information
      const palletElements = doc.querySelectorAll(".pallet");
      const palletContent: any[] = [];

      palletElements.forEach((palletEl) => {
        const palletTitle = palletEl.querySelector(".pallet-title")?.textContent?.trim() || "";
        
        // Extract table data
        const tableRows = Array.from(palletEl.querySelectorAll("table tbody tr"));
        const tableData: any[][] = [];
        
        // Add table headers
        tableData.push([
          { text: "Box Range", style: "tableHeader", alignment: "center" },
          { text: "Item", style: "tableHeader" },
          { text: "Container Type", style: "tableHeader" },
          { text: "Quantity", style: "tableHeader", alignment: "center" },
          { text: "Qty Per Box", style: "tableHeader", alignment: "center" },
          { text: "Total Quantity", style: "tableHeader", alignment: "center" },
          { text: "Box Dimensions", style: "tableHeader" },
          { text: "Box Weight", style: "tableHeader", alignment: "right" },
        ]);

        // Track active rowspans - maps cell position (0-7) to remaining rows
        const activeRowspans: Map<number, number> = new Map();
        
        // Add table body rows
        tableRows.forEach((row) => {
          const cells = Array.from(row.querySelectorAll("td"));
          const rowData: any[] = [];
          let cellIndex = 0; // Index in the actual HTML row
          
          // Process each column position (0-7)
          for (let position = 0; position < 8; position++) {
            // Check if there's an active rowspan at this position
            const remainingRows = activeRowspans.get(position);
            if (remainingRows !== undefined) {
              // This position is spanned from a previous row
              // Decrement the remaining rows
              if (remainingRows > 1) {
                activeRowspans.set(position, remainingRows - 1);
              } else {
                activeRowspans.delete(position);
              }
              // For pdfmake, when a cell has rowSpan, subsequent rows should not include
              // a cell at that position. However, to maintain array structure and avoid
              // undefined errors, we'll add an empty cell object
              const alignment = 
                position === 0 || position === 3 || position === 4 || position === 5 ? "center" :
                position === 7 ? "right" : "left";
              rowData.push({
                text: "",
                alignment,
                fontSize: 10,
              });
              continue;
            }
            
            // Check if we have a cell at this position
            if (cellIndex < cells.length) {
              const cell = cells[cellIndex];
              const text = cell.textContent?.trim() || "-";
              const rowspan = parseInt(cell.getAttribute("rowspan") || "1", 10);
              const alignment = 
                position === 0 || position === 3 || position === 4 || position === 5 ? "center" :
                position === 7 ? "right" : "left";
              
              const cellData: any = {
                text,
                alignment,
                fontSize: 10,
              };

              // If this cell has a rowspan > 1, add it to the cell and track it
              if (rowspan > 1) {
                cellData.rowSpan = rowspan;
                activeRowspans.set(position, rowspan - 1);

                // Add vertical margin for centering in rowspan cells (Box Range, Container Type, Box Dimensions, Box Weight)
                if (position === 0 || position === 2 || position === 6 || position === 7) {
                  const verticalMargin = calculateVerticalMargin(rowspan);
                  cellData.margin = [0, verticalMargin, 0, 0]; // [left, top, right, bottom]
                }
              }

              rowData.push(cellData);
              cellIndex++;
            } else {
              // No cell available - fill with empty cell to maintain 8 columns
              const alignment = 
                position === 0 || position === 3 || position === 4 || position === 5 ? "center" :
                position === 7 ? "right" : "left";
              rowData.push({
                text: "",
                alignment,
                fontSize: 10,
              });
            }
          }
          
          // Only add rows that have data
          if (rowData.length > 0) {
            tableData.push(rowData);
          }
        });

        palletContent.push(
          {
            text: palletTitle,
            style: "palletTitle",
            margin: [0, 10, 0, 8],
          },
          {
            table: {
              headerRows: 1,
              widths: ["8%", "15%", "12%", "8%", "10%", "12%", "12%", "11%"],
              body: tableData,
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => "#ddd",
              vLineColor: () => "#ddd",
              fillColor: (rowIndex: number) => (rowIndex === 0 ? "#f1f1f1" : null),
            },
            margin: [0, 0, 0, 15],
          }
        );
      });

      // Extract footer totals
      const totalWeight = doc.querySelector(".footer .totals span:first-child")?.textContent?.replace("Total Weight:", "").trim() || "0.00";
      const totalBoxes = doc.querySelector(".footer .totals span:last-child")?.textContent?.replace("Total Boxes:", "").trim() || "0";

      // Separate last pallet from others for page-break handling
      let mainPalletContent: any[] = [];
      let lastPalletTitle: any = null;
      let lastPalletTable: any = null;
      
      if (palletContent.length >= 2) {
        mainPalletContent = palletContent.slice(0, -2); // All pallets except last
        lastPalletTitle = palletContent[palletContent.length - 2]; // Second to last element
        lastPalletTable = palletContent[palletContent.length - 1]; // Last element
      } else {
        // Edge case: only one pallet or no pallets
        mainPalletContent = [];
        if (palletContent.length === 2) {
          lastPalletTitle = palletContent[0];
          lastPalletTable = palletContent[1];
        } else {
          mainPalletContent = palletContent;
        }
      }

      // Create pdfmake document definition
      const docDefinition: any = {
        pageSize: "A4",
        pageMargins: [40, 40, 40, 60],
        content: [
          // Header - Centered logo
          {
            image: logoSrc,
            width: 120,
            alignment: "center",
            margin: [0, 0, 0, 20],
          },
          
          // Details section
          {
            columns: [
              {
                width: "*",
                stack: [
                  { text: [{ text: "Date: ", bold: true }, { text: currentDate }], fontSize: 10, margin: [0, 0, 0, 5] },
                  { text: [{ text: "Sales Order No: ", bold: true }, { text: salesOrderNo }], fontSize: 10, margin: [0, 0, 0, 5] },
                  { text: [{ text: "Customer Name: ", bold: true }, { text: customerName }], fontSize: 10, margin: [0, 0, 0, 5] },
                ],
              },
              {
                width: "145",
                stack: [
                  { 
                    text: [
                      { text: "Ship To: ", bold: true },
                      { text: shipToLines.length > 0 ? shipToLines.join("\n") : "N/A" }
                    ],
                    fontSize: 10, 
                    margin: [0, 0, 0, 8],
                  },
                  { 
                    text: [
                      { text: "Ship Via: ", bold: true },
                      { text: shipVia }
                    ],
                    fontSize: 10,
                  },
                ],
              },
            ],
            margin: [0, 0, 0, 20],
          },
        //   {
        //     columns: [
        //       {
        //         width: "*",
        //         stack: [
        //           { text: [{ text: "Date: ", bold: true }, { text: currentDate }], fontSize: 10, margin: [0, 0, 0, 5] },
        //           { text: [{ text: "Sales Order No: ", bold: true }, { text: salesOrderNo }], fontSize: 10, margin: [0, 0, 0, 5] },
        //           { text: [{ text: "Customer Name: ", bold: true }, { text: customerName }], fontSize: 10, margin: [0, 0, 0, 5] },
        //           ...(customerAddressLines.length > 0 ? [
        //             { text: "Customer Address:", bold: true, fontSize: 10, margin: [0, 5, 0, 2] },
        //             ...customerAddressLines.map(line => ({ text: line, fontSize: 10, margin: [0, 0, 0, 2] }))
        //           ] : []),
        //         ],
        //       },
        //       {
        //     width: "145",
        //     stack: [
        //       { 
        //         text: [
        //           { text: "Ship To: ", bold: true },
        //           { text: shipToLines.length > 0 ? shipToLines.join("\n") : "N/A" }
        //         ],
        //         fontSize: 10, 
        //         margin: [0, 0, 0, 8],
        //       },
        //       { 
        //         text: [
        //           { text: "Ship Via: ", bold: true },
        //           { text: shipVia }
        //         ],
        //         fontSize: 10,
        //       },
        //     ],
        //   },
        // ],
        //     margin: [0, 0, 0, 20],
        //   },

          // Main pallet content (excluding last pallet)
          ...mainPalletContent,

          // Last pallet with totals - wrapped to stay together
          ...(lastPalletTitle && lastPalletTable ? [
            {
              stack: [
                lastPalletTitle,
                lastPalletTable,
                {
                  table: {
                    widths: ['*', '*'],
                    body: [
                      [
                        {
                          stack: [
                            { text: `Total Weight: ${totalWeight}`, bold: true, fontSize: 11 },
                            { text: `Total Boxes: ${totalBoxes}`, bold: true, fontSize: 11, margin: [0, 5, 0, 0] },
                          ],
                          border: [false, false, false, false],
                        },
                        {
                          text: "Packaging Manager",
                          fontSize: 11,
                          alignment: "right",
                          border: [false, false, false, false],
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                  margin: [0, 40, 0, 0],
                },
              ],
              unbreakable: true,
            },
          ] : [
            // Fallback if no last pallet - just show totals
            {
              columns: [
                {
                  width: "*",
                  stack: [
                    { text: `Total Weight: ${totalWeight}`, bold: true, fontSize: 11 },
                    { text: `Total Boxes: ${totalBoxes}`, bold: true, fontSize: 11, margin: [0, 5, 0, 0] },
                  ],
                },
                {
                  width: "*",
                  text: "Packaging Manager",
                  fontSize: 11,
                  alignment: "right",
                },
              ],
              margin: [0, 30, 0, 0],
            },
          ]),
        ],
        
        styles: {
          header: {
            fontSize: 16,
            bold: true,
            color: "#286fd1",
          },
          palletTitle: {
            fontSize: 12,
            color: "#286fd1",
          },
          tableHeader: {
            bold: true,
            fontSize: 10,
            color: "#333",
            fillColor: "#f1f1f1",
          },
        },
        
        defaultStyle: {
          fontSize: 10,
          color: "#333",
        },
      };

      // Ensure fonts are initialized
      await initializePdfMakeFonts();
      
      if (!pdfMake.vfs || Object.keys(pdfMake.vfs).length === 0) {
        console.warn("PDF fonts not fully loaded, proceeding with default fonts");
      }

      // Create and download PDF
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      const fileName = moment(new Date()).format("MM-DD-YYYY_hh-mm-ss") + "_Packing_Slip.pdf";
      pdfDocGenerator.download(fileName);
      
      toastText("Packing slip PDF downloaded successfully", "success");
      
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Something went wrong in exporting pdf report.";

      if (err?.response?.status === 400) {
        toastText("No report has been generated for this packaging details", "error");
      } else {
        toastText(errorMessage, "error");
      }
    } finally {
      setLoadingPaySlip((prev) => ({ ...prev, [packageId]: false }));
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedOrder(undefined);
  };

  const handleDeliveryOrderClick = async (
    salesOrder: string,
    packageId: string,
    date: string
  ) => {
    try {
      setLoadingDeliveryOrder((prev) => ({ ...prev, [packageId]: true }));
      // Make API call for each packageId (similar to sales order click)
      const response: any = await getApi(
        `/shipping/packaginglist?packageId=${packageId}`
      );
      const data = response.data.data;
      
      // Extract shipping method from sales orders
      let shippingMethod = null;
      if (data.salesOrders && data.salesOrders.length > 0) {
        const firstOrder = data.salesOrders[0];
        if (firstOrder.shippingMethod) {
          try {
            const method = typeof firstOrder.shippingMethod === 'string' 
              ? JSON.parse(firstOrder.shippingMethod) 
              : firstOrder.shippingMethod;
            shippingMethod = method?.FullName || null;
          } catch (e) {
            console.error("Error parsing shipping method:", e);
          }
        }
      }
      
      // Extract bondedDetails - it could be a string or an object
      let bondedDetails = null;
      if (data.bondedDetails) {
        if (typeof data.bondedDetails === 'string') {
          bondedDetails = data.bondedDetails;
        } else if (typeof data.bondedDetails === 'object') {
          // If it's an object, try to extract a value or stringify
          // For now, if it's an object, we'll stringify it
          // But typically bondedDetails should be a simple string
          bondedDetails = JSON.stringify(data.bondedDetails);
        }
      }
      
      // Extract customerAddress from API response
      const customerAddress = data.customerAddress || null;
      
      setDeliveryOrderData({
        salesOrder,
        packageId,
        shippingMethod,
        date,
        bondedDetails,
        customerAddress,
      });
      setIsDeliveryOrderModalVisible(true);
    } catch (error) {
      console.error("Error fetching delivery order data:", error);
      toastText("Failed to load delivery order data", "error");
    } finally {
      setLoadingDeliveryOrder((prev) => ({ ...prev, [packageId]: false }));
    }
  };
  const calculateVerticalMargin = (rowSpanCount: number) => {
  if (rowSpanCount === 1) return 0;
  if (rowSpanCount === 2) return 6;
  if (rowSpanCount === 3) return 12;
  return (rowSpanCount - 1) * 6;
};

  const handleDeliveryOrderModalClose = () => {
    setIsDeliveryOrderModalVisible(false);
    setDeliveryOrderData(null);
  };

  const handleDeliveryOrderPrint = async (bondedDetails: string, date: string) => {
    if (!deliveryOrderData) return;
    
    try {
      setLoadingPrint(true);
      
      // Call the API to update bonded details
      await putApi("/shipping/update-bonded-details", {
        packageId: deliveryOrderData.packageId,
        bondedDetails: bondedDetails,
      });
      
      // Fetch packaging data for PDF generation
      const response: any = await getApi(
        `/shipping/packaginglist?packageId=${deliveryOrderData.packageId}`
      );
      if (!response || response.status !== 200 || !response.data?.data) {
        throw new Error("Failed to fetch the packaging data.");
      }
      const data = response.data.data;
      
      // Generate HTML for delivery order with the selected date
      const html = generateDeliveryOrderHTML({
        ...data,
        bondedDetails: bondedDetails,
        date: date,
      }, "");
  
      // Parse HTML to extract content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      
      // Helper function to convert images to base64
      const convertImageUrlToBase64 = async (url: string): Promise<string> => {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.warn("Failed to convert image to base64:", error);
          return url;
        }
      };
  
      // Process images - convert to base64
      const images = Array.from(doc.querySelectorAll("img"));
      for (const img of images) {
        const src = img.getAttribute("src");
        if (src && !src.startsWith("data:")) {
          try {
            const base64 = await convertImageUrlToBase64(src);
            img.setAttribute("src", base64);
          } catch (error) {
            console.warn("Failed to convert image:", src, error);
          }
        }
      }
  
      // Manual conversion to pdfmake format with proper styling
      const mainContent = doc.querySelector(".main-content");
      if (!mainContent) {
        throw new Error("Could not find main content element");
      }
  
      // Extract data from HTML for manual PDF construction
      const currentDate = doc.querySelector(".details .section1 p:first-child strong")?.nextSibling?.textContent?.trim() || "-";
      const salesOrderNo = doc.querySelector(".details .section1 p:nth-child(2) strong")?.nextSibling?.textContent?.trim() || "-";
      const customerName = doc.querySelector(".details .section1 p:nth-child(3) strong")?.nextSibling?.textContent?.trim() || "-";
      
      // Extract customer address
      const customerAddressElement = doc.querySelector(".details .section1 p:nth-child(4)");
      let customerAddressLines: string[] = [];
      if (customerAddressElement) {
        const addressHtml = customerAddressElement.innerHTML;
        const addressParts = addressHtml.split('<br>');
        customerAddressLines = addressParts
          .map(part => part.replace(/<[^>]*>/g, '').trim())
          .filter(part => part && !part.startsWith('Customer Address:'));
      }
  
      // Extract shipping info
      const shipToElement = doc.querySelector(".details .section2 p:first-child");
      let shipToLines: string[] = [];
      if (shipToElement) {
        const shipToHtml = shipToElement.innerHTML;
        const parts = shipToHtml.split('<br>');
        shipToLines = parts
          .map((part, index) => {
            const cleaned = part.replace(/<[^>]*>/g, '').trim();
            // For the first part, extract text after "Ship To:"
            if (index === 0 && cleaned.includes('Ship To:')) {
              const afterShipTo = cleaned.split('Ship To:')[1]?.trim();
              return afterShipTo || null;
            }
            return cleaned;
          })
          .filter(part => part && part.length > 0);
      }

      const shipVia = doc.querySelector(".details .section2 p:nth-child(2) strong")?.nextSibling?.textContent?.trim() || "N/A";
  
      // Extract pallet information
      const palletElements = doc.querySelectorAll(".pallet");
      const palletContent: any[] = [];
      const palletSummaryData: Array<{
        name: string;
        dimensions: string;
        weight: string;
        totalBoxes: string;
      }> = [];

      palletElements.forEach((palletEl, palletIndex) => {
        const palletTitle = palletEl.querySelector(".pallet-title")?.textContent?.trim() || "";
        
        // Extract pallet summary data from title (format: "Pallet X (Mix) - Dimensions: LxWxH, Weight: W, Total Boxes: B")
        const titleMatch = palletTitle.match(/Pallet\s+(\d+)\s*(\(Mix\))?\s*-\s*Dimensions:\s*([^,]+),\s*Weight:\s*([^,]+),\s*Total Boxes:\s*(.+)/);
        if (titleMatch) {
          const palletNum = titleMatch[1];
          const isMix = titleMatch[2] ? " (Mix)" : "";
          const dimensions = titleMatch[3].trim();
          const weight = titleMatch[4].trim();
          const totalBoxes = titleMatch[5].trim();
          
          palletSummaryData.push({
            name: `Pallet ${palletNum}${isMix}`,
            dimensions,
            weight,
            totalBoxes,
          });
        }
        
        // Extract table data
        const tableRows = Array.from(palletEl.querySelectorAll("table tbody tr"));
        const tableData: any[][] = [];
        
        // Add table headers
        tableData.push([
          { text: "Box Range", style: "tableHeader", alignment: "center" },
          { text: "Item", style: "tableHeader" },
          { text: "Container Type", style: "tableHeader" },
          { text: "Quantity", style: "tableHeader", alignment: "center" },
          { text: "Qty Per Box", style: "tableHeader", alignment: "center" },
          { text: "Total Quantity", style: "tableHeader", alignment: "center" },
          { text: "Box Dimensions", style: "tableHeader", },
          { text: "Box Weight", style: "tableHeader", alignment: "left" },
        ]);

        // Track active rowspans - maps cell position (0-7) to remaining rows
        const activeRowspans: Map<number, number> = new Map();
        
        // Add table body rows
        tableRows.forEach((row) => {
          const cells = Array.from(row.querySelectorAll("td"));
          const rowData: any[] = [];
          let cellIndex = 0; // Index in the actual HTML row
          
          // Process each column position (0-7)
          for (let position = 0; position < 8; position++) {
            // Check if there's an active rowspan at this position
            const remainingRows = activeRowspans.get(position);
            if (remainingRows !== undefined) {
              // This position is spanned from a previous row
              // Decrement the remaining rows
              if (remainingRows > 1) {
                activeRowspans.set(position, remainingRows - 1);
              } else {
                activeRowspans.delete(position);
              }
              // For pdfmake, when a cell has rowSpan, subsequent rows should not include
              // a cell at that position. However, to maintain array structure and avoid
              // undefined errors, we'll add an empty cell object
              const alignment = 
                position === 0 || position === 3 || position === 4 || position === 5 ? "center" :
                position === 7 ? "right" : "left";
              rowData.push({
                text: "",
                alignment,
                fontSize: 10,
              });
              continue;
            }
            
            // Check if we have a cell at this position
            if (cellIndex < cells.length) {
              const cell = cells[cellIndex];
              const text = cell.textContent?.trim() || "-";
              const rowspan = parseInt(cell.getAttribute("rowspan") || "1", 10);
              const alignment = 
                position === 0 || position === 3 || position === 4 || position === 5 ? "center" :
                position === 7 ? "left" : "left";
              
              const cellData: any = {
                text,
                alignment,
                fontSize: 10,
              };

              // If this cell has a rowspan > 1, add it to the cell and track it
              if (rowspan > 1) {
                cellData.rowSpan = rowspan;
                activeRowspans.set(position, rowspan - 1);

                // Add vertical margin for centering in rowspan cells (Box Range, Container Type, Box Dimensions, Box Weight)
                if (position === 0 || position === 2 || position === 6 || position === 7) {
                  const verticalMargin = calculateVerticalMargin(rowspan);
                  cellData.margin = [0, verticalMargin, 0, 0]; // [left, top, right, bottom]
                }
              }
              
              rowData.push(cellData);
              cellIndex++;
            } else {
              // No cell available - fill with empty cell to maintain 8 columns
              const alignment = 
                position === 0 || position === 3 || position === 4 || position === 5 ? "center" :
                position === 7 ? "left" : "";
              rowData.push({
                text: "",
                alignment,
                fontSize: 10,
              });
            }
          }
          
          // Only add rows that have data
          if (rowData.length > 0) {
            tableData.push(rowData);
          }
        });

        const isLastPallet = palletIndex === palletElements.length - 1;
        
        // For the last pallet, wrap it with totals in a page-break-inside: avoid group
        if (isLastPallet) {
          palletContent.push(
            {
              text: palletTitle,
              style: "palletTitle",
              margin: [0, 10, 0, 8],
            },
            {
              table: {
                headerRows: 1,
                widths: ["10%", "15%", "12%", "8%", "10%", "12%", "15%", "11%"],
                body: tableData,
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => "#ddd",
                vLineColor: () => "#ddd",
                fillColor: (rowIndex: number) => (rowIndex === 0 ? "#f1f1f1" : null),
              },
              margin: [0, 0, 0, 15],
              pageBreak: 'avoid',
            }
          );
        } else {
          palletContent.push(
            {
              text: palletTitle,
              style: "palletTitle",
              margin: [0, 10, 0, 8],
            },
            {
              table: {
                headerRows: 1,
                widths: ["10%", "15%", "12%", "8%", "10%", "12%", "15%", "11%"],
                body: tableData,
              },
              layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => "#ddd",
                vLineColor: () => "#ddd",
                fillColor: (rowIndex: number) => (rowIndex === 0 ? "#f1f1f1" : null),
              },
              margin: [0, 0, 0, 15],
            }
          );
        }
      });
  
      // Extract footer totals
      const totalWeight = doc.querySelector(".footer .totals span:first-child")?.textContent?.replace("Total Weight:", "").trim() || "0.00";
      const totalBoxes = doc.querySelector(".footer .totals span:last-child")?.textContent?.replace("Total Boxes:", "").trim() || "0";

      // Get logo image
      const logoImg = doc.querySelector(".header-left img");
      const logoSrc = logoImg?.getAttribute("src") || "";

      // Separate last pallet from others for page-break handling
      // Each pallet has 2 elements: title and table, so last pallet is last 2 elements
      let mainPalletContent: any[] = [];
      let lastPalletTitle: any = null;
      let lastPalletTable: any = null;
      
      if (palletContent.length >= 2) {
        mainPalletContent = palletContent.slice(0, -2); // All pallets except last
        lastPalletTitle = palletContent[palletContent.length - 2]; // Second to last element
        lastPalletTable = palletContent[palletContent.length - 1]; // Last element
      } else {
        // Edge case: only one pallet or no pallets
        mainPalletContent = [];
        if (palletContent.length === 2) {
          lastPalletTitle = palletContent[0];
          lastPalletTable = palletContent[1];
        } else {
          mainPalletContent = palletContent;
        }
      }

      // Create Delivery Summary page content
      const deliverySummaryContent = [
        // Page break before Delivery Summary (always last page)
        { text: "Delivery Summary", pageBreak: 'before', fontSize: 18, bold: true, alignment: "left", margin: [0, 0, 0, 20] },
        
        // Summary Box with border
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  stack: [
                    { text: [{ text: "Sales Order No: ", bold: true }, { text: salesOrderNo }], fontSize: 11, margin: [10, 10, 10, 5] },
                    { text: [{ text: "Date: ", bold: true }, { text: currentDate }], fontSize: 11, margin: [10, 0, 10, 5] },
                    { text: [{ text: "Customer Name: ", bold: true }, { text: customerName }], fontSize: 11, margin: [10, 0, 10, 5] },
                    ...(customerAddressLines.length > 0 ? [
                      { text: "Customer Address:", bold: true, fontSize: 11, margin: [10, 5, 10, 2] },
                      ...customerAddressLines.map(line => ({ text: line, fontSize: 11, margin: [10, 0, 10, 2] }))
                    ] : []),
                    { text: "Ship To:", bold: true, fontSize: 11, margin: [10, 8, 10, 2] },
                    ...(shipToLines.length > 0 ? shipToLines.map(line => ({ text: line, fontSize: 11, margin: [10, 0, 10, 2] })) : [{ text: "N/A", fontSize: 11, margin: [10, 0, 10, 2] }]),
                    { text: `Total Boxes: ${totalBoxes}`, fontSize: 11, margin: [10, 8, 10, 5] },
                    { text: `Total Weight: ${totalWeight}`, fontSize: 11, margin: [10, 0, 10, 10] },
                    { 
                      text: "The undersigned confirms receipt of the goods as summarized above and detailed on previous pages.", 
                      fontSize: 10, 
                      italics: true, 
                      margin: [10, 5, 10, 10] 
                    },
                  ],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => "#333",
            vLineColor: () => "#333",
          },
          margin: [0, 0, 0, 20],
        },

        // Pallet Summary Section
        {
          text: "Pallet Summary:",
          fontSize: 12,
          bold: true,
          margin: [0, 20, 0, 10],
        },
        ...palletSummaryData.map((pallet) => ({
          text: `${pallet.name}: Dimensions: ${pallet.dimensions}, Weight: ${pallet.weight}, Total Boxes: ${pallet.totalBoxes}`,
          fontSize: 11,
          margin: [0, 0, 0, 5],
        })),
      ];

      // Create pdfmake document definition with proper styling
      const docDefinition: any = {
        pageSize: "A4",
        pageMargins: [40, 40, 40, 100],
        content: [
          // Header
          {
            columns: [
              {
                image: logoSrc,
                width: 120,
                alignment: "left",
              },
              {
                text: "DELIVERY ORDER",
                style: "header",
                alignment: "right",
                margin: [0, 10, 0, 0],
              },
            ],
            margin: [0, 0, 0, 20],
          },
          
          // Shipper and Bonded section
          {
            columns: [
              {
                width: "*",
                stack: [
                  { text: "Shipper/Pickup:", bold: true, fontSize: 11 },
                  { text: "3nStar Inc.", fontSize: 10 },
                  { text: "10813 NW 30th Street, Suite #110", fontSize: 10 },
                  { text: "Doral, FL, 33172", fontSize: 10 },
                ],
              },
              {
                width: "145",
                text: [
                  // { text: "Bonded: ", bold: true, fontSize: 10 },
                  { text: bondedDetails || "", fontSize: 10 }
                ],
                alignment: "left",
              },
            ],
            margin: [0, 0, 0, 20],
          },

          // Details section
          {
            columns: [
              {
                width: "*",
                stack: [
                  { text: [{ text: "Date: ", bold: true }, { text: currentDate }], fontSize: 10, margin: [0, 0, 0, 5] },
                  { text: [{ text: "Sales Order No: ", bold: true }, { text: salesOrderNo }], fontSize: 10, margin: [0, 0, 0, 5] },
                  { text: [{ text: "Customer Name: ", bold: true }, { text: customerName }], fontSize: 10, margin: [0, 0, 0, 5] },
                  ...(customerAddressLines.length > 0 ? [
                    { text: "Customer Address:", bold: true, fontSize: 10, margin: [0, 5, 0, 2] },
                    ...customerAddressLines.map(line => ({ text: line, fontSize: 10, margin: [0, 0, 0, 2] }))
                  ] : []),
                ],
              },
              {
            width: "145",
            stack: [
              { 
                text: [
                  { text: "Ship To: ", bold: true },
                  { text: shipToLines.length > 0 ? shipToLines.join("\n") : "N/A" }
                ],
                fontSize: 10, 
                margin: [0, 0, 0, 8],
              },
              { 
                text: [
                  { text: "Ship Via: ", bold: true },
                  { text: shipVia }
                ],
                fontSize: 10,
              },
            ],
          },
        ],
            margin: [0, 0, 0, 20],
          },

          // Main pallet content (excluding last pallet)
          ...mainPalletContent,

          // Last pallet with totals - wrapped to stay together
          ...(lastPalletTitle && lastPalletTable ? [
            {
              stack: [
                lastPalletTitle,
                lastPalletTable,
                {
                  table: {
                    widths: ['*', '*'],
                    body: [
                      [
                        {
                          stack: [
                            { text: `Total Weight: ${totalWeight}`, bold: true, fontSize: 11 },
                            { text: `Total Boxes: ${totalBoxes}`, bold: true, fontSize: 11, margin: [0, 5, 0, 0] },
                          ],
                          border: [false, false, false, false],
                        },
                        {
                          text: "Packaging Manager",
                          fontSize: 11,
                          alignment: "right",
                          border: [false, false, false, false],
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                  margin: [0, 40, 0, 0],//margin bottom 40px
                  // pageBreak: 'avoid',
                },
              ],
              unbreakable: true,
            },
          ] : [
            // Fallback if no last pallet - just show totals
            {
              columns: [
                {
                  width: "*",
                  stack: [
                    { text: `Total Weight: ${totalWeight}`, bold: true, fontSize: 11 },
                    { text: `Total Boxes: ${totalBoxes}`, bold: true, fontSize: 11, margin: [0, 5, 0, 0] },
                  ],
                },
                {
                  width: "*",
                  text: "Packaging Manager",
                  fontSize: 11,
                  alignment: "right",
                },
              ],
              margin: [0, 30, 0, 0],
            },
          ]),

          // Delivery Summary Page (always last - page break is in first element)
          ...deliverySummaryContent,
        ],
        
        // Footer with signature section - only on last page (Delivery Summary)
        footer: (currentPage: number, pageCount: number) => {
          if (currentPage === pageCount) {
            return {
              columns: [
                {
                  width: "*",
                  stack: [
                    { text: "Driver (Print Name): _____________________", fontSize: 10, margin: [0, 0, 0, 8] },
                    { text: "Date: _____________________", fontSize: 10, margin: [0, 0, 0, 8] },
                    { text: "Time: _____________________", fontSize: 10 },
                  ],
                },
                {
                  width: "*",
                  stack: [
                    { text: "Received By (Print Name): _____________________", fontSize: 10, margin: [0, 0, 0, 8] },
                    { text: "Date: _____________________", fontSize: 10, margin: [0, 0, 0, 8] },
                    { text: "Time: _____________________", fontSize: 10 },
                  ],
                },
              ],
              margin: [40, 20, 40, 30],
            };
          }
          return null;
        },
  
        styles: {
          header: {
            fontSize: 16,
            bold: true,
            color: "#333",
          },
          palletTitle: {
            fontSize: 12,
            color: "#286fd1",
          },
          tableHeader: {
            bold: true,
            fontSize: 10,
            color: "#333",
            fillColor: "#f1f1f1",
          },
        },
        
        defaultStyle: {
          fontSize: 10,
          color: "#333",
        },
      };
  
      // Ensure fonts are initialized
      await initializePdfMakeFonts();
      
      if (!pdfMake.vfs || Object.keys(pdfMake.vfs).length === 0) {
        console.warn("PDF fonts not fully loaded, proceeding with default fonts");
      }
  
      // Create and download PDF
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      const fileName = moment(new Date()).format("MM-DD-YYYY_hh-mm-ss") + "_Delivery_Order.pdf";
      pdfDocGenerator.download(fileName);
      
      toastText("Delivery order PDF downloaded successfully", "success");
      handleDeliveryOrderModalClose();
      
    } catch (error: any) {
      console.error("Error generating delivery order PDF:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate delivery order PDF";
      toastText(errorMessage, "error");
    } finally {
      setLoadingPrint(false);
    }
  };

  const getRowSpan = (id: string, index: number | undefined) => {
    if (index === undefined) return 0;
    const groupedRows = salesOrderData.filter(
      (item: any) => item.packageId === id
    );
    const firstRowIndex = salesOrderData.findIndex(
      (item: any) => item.packageId === id
    );
    return index === firstRowIndex ? groupedRows.length : 0;
  };

  const deleteDataHandler = (packing: any) => {
    setSelectedPacking(packing);
  };

  return (
    <div className={styles["shipment-table"]}>
      <div className={styles["shipment-table__wrapper"]}>
        <SearchAndFilter
          performSearchHandler={performSearchHandler}
          searchValue={searchValue}
        />
        <Table
          dataSource={salesOrderData}
          rowKey={(record) => record.id}
          pagination={false}
          scroll={{ y: "calc(100vh - 350px)" }}
          className="table-global"
          loading={isLoading}
          bordered
          onChange={tableChangeHandler}
        >
          <Column
            title="Sales Order"
            dataIndex="salesOrder"
            key="salesOrder"
            width="10%"
            className="bg-white"
            render={(salesOrder: string, record: any) => {
              const relatedSalesOrders = salesOrderData
                .filter((item) => item.packageId === record.packageId)
                .map((item) => item.salesOrder)
                .join(", ");
              return (
                <a
                  onClick={() =>
                    handleSalesOrderClick(relatedSalesOrders, record.packageId)
                  }
                >
                  {relatedSalesOrders}
                </a>
              );
            }}
            onCell={(record: any, index: number | undefined) => ({
              rowSpan:
                index !== undefined ? getRowSpan(record.packageId, index) : 0,
            })}
          />
          <Column
            title="Date"
            dataIndex="date"
            key="date"
            width="10%"
            className="bg-white"
            render={(text: string) =>
              text ? moment(text).format("MM/DD/YYYY") : "-"
            }
          />
          <Column
            title="Shipping Method"
            dataIndex="shippingMethod"
            key="shippingMethod"
            width="15%"
            className="bg-white"
            render={(text: string) => (invalidText(text) ? "-" : text)}
          />
          <Column
            title="Notes"
            dataIndex="notes"
            key="notes"
            width="15%"
            className="bg-white"
            render={(text: string) => (invalidText(text) ? "-" : text)}
          />
          <Column
            title="Status"
            dataIndex="status"
            key="status"
            width="10%"
            className="bg-white"
            render={(_: any, record: any, index: number) => {
              const rowSpan = getRowSpan(record.packageId, index);
              const formattedStatus = record.status
                ?.replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (char: any) => char.toUpperCase());
              return rowSpan > 0 ? (
                <>
                  <Tag
                    color={
                      SALES_ORDER_STATUS[
                        record.status as keyof typeof SALES_ORDER_STATUS
                      ]?.color
                    }
                  >
                    {formattedStatus}
                  </Tag>
                </>
              ) : null;
            }}
            onCell={(_: any, index: number | undefined) => ({
              rowSpan:
                index !== undefined
                  ? getRowSpan(salesOrderData[index].packageId, index)
                  : 0,
            })}
          />
          <Column
            title="Last Modified"
            dataIndex="updatedAt"
            key="updatedAt"
            width="10%"
            className="bg-white"
            sorter={(a: any, b) => {
              const dateA = new Date(a.salesOrderDate).getTime();
              const dateB = new Date(b.salesOrderDate).getTime();
              return dateA - dateB; // Sort dates in ascending order
            }}
            sortDirections={["ascend", "descend", "ascend"]}
            // eslint-disable-next-line react/jsx-no-undef
            sortIcon={(data) => <SortingIcon data={data} />}
            render={(text: string) =>
              text ? moment(text).format("MM/DD/YYYY") : "-"
            }
          />
          {/* New Column for Generate Pay Slip */}
          <Column
            title="Packing Slip"
            key="generatePaySlip"
            width="8%"
            className="bg-white"
            render={(_: any, record: any, index: number) => {
              const rowSpan = getRowSpan(record.packageId, index); // Use the same logic as "Status"
              return rowSpan > 0 ? (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => exportPdf(record.packageId)} // Pass packageId to exportPdf
                  loading={!!loadingPaySlip[record.packageId]}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }} // Center the icon
                />
              ) : null; // Hide the button for rows that are spanned
            }}
            onCell={(_: any, index: number | undefined) => ({
              rowSpan:
                index !== undefined
                  ? getRowSpan(salesOrderData[index].packageId, index)
                  : 0,
            })}
          />
          <Column
            title="Delivery Order"
            key="deliveryOrder"
            width="10%"
            className="bg-white"
            render={(_: any, record: any, index: number) => {
              const rowSpan = getRowSpan(record.packageId, index); // Use the same logic as "Status"
              const relatedSalesOrders = salesOrderData
                .filter((item) => item.packageId === record.packageId)
                .map((item) => item.salesOrder)
                .join(", ");
              return rowSpan > 0 ? (
                <Button
                  type="primary"
                  icon={<FileOutlined />}
                  onClick={() =>
                    handleDeliveryOrderClick(relatedSalesOrders, record.packageId, record.date)
                  }
                  loading={!!loadingDeliveryOrder[record.packageId]}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: "green",
                    alignItems: "center",
                  }} // Center the icon
                />
              ) : null; // Hide the button for rows that are spanned
            }}
            onCell={(_: any, index: number | undefined) => ({
              rowSpan:
                index !== undefined
                  ? getRowSpan(salesOrderData[index].packageId, index)
                  : 0,
            })}
          />
          <Column
            title="Action"
            dataIndex="action"
            key="action"
            className="bg-white"
            width={"8%"}
            render={(_: any, record: any, index: number) => {
              const rowSpan = getRowSpan(record.packageId, index); // Use the same logic as "Status"
              return rowSpan > 0 ? (
                <Space size={20}>
                  <div
                    className={`cursor-pointer flex align-center justify-center`}
                    onClick={() => {
                      deleteDataHandler(record);
                      showModal();
                    }}
                  >
                    <DeleteActionSvg />
                  </div>
                </Space>
              ) : null; // Hide the button for rows that are spanned
            }}
            onCell={(_: any, index: number | undefined) => ({
              rowSpan:
                index !== undefined
                  ? getRowSpan(salesOrderData[index].packageId, index)
                  : 0,
            })}
          />
        </Table>
        <PackagingListModal
          isVisible={isModalVisible}
          packagingData={packagingData}
          onClose={handleModalClose}
          onGeneratePaySlip={exportPdf} // Use exportPdf directly
          packageId={selectedPackageId}
          loadingPaySlip={loadingPaySlip}
        />
        {deliveryOrderData && (
          <PrintDeliveryOrderModal
            isVisible={isDeliveryOrderModalVisible}
            onClose={handleDeliveryOrderModalClose}
            onPrint={handleDeliveryOrderPrint}
            salesOrder={deliveryOrderData.salesOrder}
            packageId={deliveryOrderData.packageId}
            shippingMethod={deliveryOrderData.shippingMethod}
            date={deliveryOrderData.date}
            bondedDetails={deliveryOrderData.bondedDetails}
            customerAddress={deliveryOrderData.customerAddress}
            loading={loadingPrint}
          />
        )}
      </div>
    </div>
  );
};

export default CompletedSalesOrderTable;
